import { FC, useMemo, useState, useEffect, useContext, useCallback } from 'react';
import { layer, source, feature, MapContext } from 'react-openlayers';
import { BoundaryApprovalJob } from '../domain/BoundaryApprovalJob';
import { Coordinate } from 'ol/coordinate';
import { convertProjection4329 } from '../../utils';
import { YELLOW, LIGHT_BLUE, BLACK } from '../../rgbColors';
import { MapZoomStorage } from '../../db/local_storage';
import { parseGeoJsonText } from '../../utilities/geojson-utils';
import { GeoDataUtilities } from '@rogoag/shared-ts';

interface BoundaryApprovalZonesLayerProps {
  jobs: BoundaryApprovalJob[];
  activeJobId: string | null;
  onJobDoubleClick?: (jobId: string) => void;
}

const MIN_ZOOM_FOR_LABELS = 12;

/**
 * Map layer that displays FIELD and COLLECTED_FIELD zones from all visible non-active missions
 * These zones are read-only and cannot be modified
 */
export const BoundaryApprovalZonesLayer: FC<BoundaryApprovalZonesLayerProps> = ({
  jobs,
  activeJobId,
  onJobDoubleClick,
}) => {
  const [zoomLevel, setZoomLevel] = useState(MapZoomStorage.get().value);
  const mapContext = useContext(MapContext);

  // Handle double-click on map to focus job
  const handleMapDoubleClick = useCallback(
    (event: any) => {
      const map = mapContext?.map;

      if (!map || !onJobDoubleClick) {
        return;
      }

      const pixel = map.getEventPixel(event.originalEvent);
      const features = map.getFeaturesAtPixel(pixel);

      // Find the first feature that belongs to a non-active job
      const feature = features?.find((feat: any) => {
        const props = feat.getProperties();

        return props.jobId && props.readOnly && props.jobId !== activeJobId;
      });

      if (feature) {
        const jobId = feature.get('jobId');

        if (jobId) {
          onJobDoubleClick(jobId);
        }
      }
    },
    [mapContext, onJobDoubleClick, activeJobId],
  );

  // Attach double-click listener
  useEffect(() => {
    const map = mapContext?.map;

    if (!map) {
      return;
    }

    map.on('dblclick', handleMapDoubleClick);

    return () => {
      map.un('dblclick', handleMapDoubleClick);
    };
  }, [mapContext, handleMapDoubleClick]);

  // Listen to zoom changes
  useEffect(() => {
    const interval = setInterval(() => {
      const newZoom = MapZoomStorage.get().value;

      if (newZoom !== zoomLevel) {
        setZoomLevel(newZoom);
      }
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [zoomLevel]);

  const { zoneFeatures, labelFeatures } = useMemo(() => {
    const allZoneFeatures: JSX.Element[] = [];
    const allLabelFeatures: JSX.Element[] = [];

    /**
     * Helper to create a polygon feature
     */
    const createPolygonFeature = (
      coords: Coordinate[][],
      key: string,
      strokeColor: number[],
      fillColor: number[],
      featureName: string,
      job: BoundaryApprovalJob,
    ) => {
      return (
        <feature.PolygonReact
          key={key}
          coordinates={coords}
          strokeOptions={{ color: strokeColor, width: 2 }}
          fillOptions={{ color: fillColor }}
          properties={{
            name: featureName,
            jobId: job.id,
            jobName: job.name,
            readOnly: true,
          }}
          zIndex={0}
        />
      );
    };

    /**
     * Helper to create a label feature if conditions are met
     */
    const tryCreateLabelFeature = (
      coords: Coordinate[][],
      job: BoundaryApprovalJob,
      isFieldZone: boolean,
    ): JSX.Element | null => {
      if (!isFieldZone || zoomLevel < MIN_ZOOM_FOR_LABELS) {
        return null;
      }

      const center = GeoDataUtilities.calculatePolygonCenter(coords[0]);

      if (!center) {
        return null;
      }

      return (
        <feature.PointReact
          key={`label-${job.id}`}
          coordinate={center}
          textOptions={{
            text: job.name,
            font: 'Bold 14px Arial',
            fillOptions: { color: BLACK },
            strokeOptions: { color: [255, 255, 255, 1], width: 3 },
            overflow: true,
          }}
          properties={{
            jobId: job.id,
            jobName: job.name,
            readOnly: true,
          }}
          zIndex={1}
        />
      );
    };

    jobs.forEach((job) => {
      // Skip if job is not visible or is the active job
      if (!job.isVisible || job.id === activeJobId) {
        return;
      }

      // Get the GeoJSON text from the job record
      const geoJsonText = job.jobRecord.get('Bnd GeoJSON Text');

      if (!geoJsonText || typeof geoJsonText !== 'string') {
        return;
      }

      const geoJson = parseGeoJsonText(geoJsonText);

      if (!geoJson) {
        console.error(`Failed to parse Bnd GeoJSON Text for job ${job.id}`);

        return;
      }

      // Track if we've added a label for this job yet
      let labelAdded = false;

      // Process each feature in the GeoJSON
      geoJson.features.forEach((geoFeature, featureIndex) => {
        const geometry = geoFeature.geometry;

        // We only handle Polygon and MultiPolygon
        if (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon') {
          return;
        }

        // Determine if this is a FIELD zone or COLLECTED_FIELD zone
        const isField = geoFeature.properties?.name === 'field';
        const isCollectedField = geoFeature.properties?.name === 'collected field';

        // Do not render other zone types
        if (!isField && !isCollectedField) {
          return;
        }

        const strokeColor = isCollectedField ? LIGHT_BLUE : YELLOW;
        const fillColor = [...strokeColor, 0.1]; // 10% opacity
        const featureName = geoFeature.properties?.name || 'field';

        // Normalize to array of polygon coordinates (MultiPolygon is already an array)
        const polygonCoordsList = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;

        // Process each polygon
        polygonCoordsList.forEach((polygonCoords, polyIndex) => {
          const coords: Coordinate[][] = polygonCoords.map((ring) => ring.map((coord) => convertProjection4329(coord)));

          const key =
            geometry.type === 'Polygon'
              ? `zone-${job.id}-polygon-${featureIndex}`
              : `zone-${job.id}-multipolygon-${featureIndex}-${polyIndex}`;

          allZoneFeatures.push(createPolygonFeature(coords, key, strokeColor, fillColor, featureName, job));

          // Add label only once per job, only for the first polygon, and only if field zone
          if (!labelAdded && polyIndex === 0 && isField) {
            const labelFeature = tryCreateLabelFeature(coords, job, isField);

            if (labelFeature) {
              allLabelFeatures.push(labelFeature);
              labelAdded = true;
            }
          }
        });
      });
    });

    return { zoneFeatures: allZoneFeatures, labelFeatures: allLabelFeatures };
  }, [jobs, activeJobId, zoomLevel]);

  // If no features, don't render the layer
  if (zoneFeatures.length === 0) {
    return null;
  }

  return (
    // @ts-ignore
    <layer.Vector zIndex={0}>
      {/* @ts-ignore */}
      <source.VectorSourceReact>
        {zoneFeatures}
        {labelFeatures}
      </source.VectorSourceReact>
    </layer.Vector>
  );
};
